import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { cn } from "@/lib/utils";

import { CHART_COLORS, paletteAt } from "./palette";

export interface DonutChartDatum {
  label: string;
  value: number;
}

interface DonutChartProps {
  data: DonutChartDatum[];
  /** Format do valor no tooltip / legenda. */
  valueFormatter?: (value: number) => string;
  /** Mostra legenda à direita (default true). */
  showLegend?: boolean;
  /** Texto exibido no centro do donut (ex.: total). */
  centerLabel?: string;
  centerValue?: string;
}

/**
 * Donut chart (pie com hole). Pares (label, value); legenda lateral
 * mostra label + valor + % do total.
 */
export function DonutChart({
  data,
  valueFormatter = (v) => String(v),
  showLegend = true,
  centerLabel,
  centerValue,
}: DonutChartProps) {
  const total = data.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
  return (
    <div className="flex h-full w-full flex-col gap-3 lg:flex-row lg:items-center">
      <div className="relative h-full flex-1 min-h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="60%"
              outerRadius="90%"
              stroke="#fff"
              strokeWidth={2}
              paddingAngle={data.length > 1 ? 1 : 0}
            >
              {data.map((_, idx) => (
                <Cell key={`c-${idx}`} fill={paletteAt(idx)} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 12,
                border: `1px solid ${CHART_COLORS.line}`,
                background: "#fff",
                fontSize: 12,
                boxShadow: "0 6px 16px rgba(15, 14, 29, 0.08)",
              }}
              formatter={(value, name) => [
                valueFormatter(Number(value ?? 0)),
                String(name ?? ""),
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        {centerLabel || centerValue ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            {centerLabel ? (
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
                {centerLabel}
              </span>
            ) : null}
            {centerValue ? (
              <span className="tabular text-lg font-semibold text-ink-950">
                {centerValue}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      {showLegend ? (
        <ul
          className={cn(
            "flex flex-col gap-1.5 text-xs lg:max-w-[55%]",
            "lg:max-h-full lg:overflow-y-auto",
          )}
        >
          {data.map((d, idx) => {
            const pct = total ? (d.value / total) * 100 : 0;
            return (
              <li
                key={`${d.label}-${idx}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: paletteAt(idx) }}
                  />
                  <span className="truncate text-ink-900" title={d.label}>
                    {d.label}
                  </span>
                </span>
                <span className="tabular shrink-0 text-ink-700">
                  {valueFormatter(d.value)}{" "}
                  <span className="text-ink-500">({pct.toFixed(1)}%)</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
